import { useId } from 'react'

export default function Logo({ variant = 'mark', size, className = '', transparent = false }) {
  const rawId = useId()
  const id = `logo_${rawId.replace(/[^a-zA-Z0-9]/g, '_')}`

  if (variant === 'mark') {
    const s = size ?? 24
    const glow = `url(#${id}-glow)`
    const ng   = `url(#${id}-ng)`
    const p    = `url(#${id}-p)`
    return (
      <svg viewBox="0 0 100 100" width={s} height={s} className={className}>
        <defs>
          <filter id={`${id}-glow`}>
            <feGaussianBlur stdDeviation="2.5" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
          <radialGradient id={`${id}-ng`} cx="30%" cy="30%" r="70%">
            <stop offset="0%"   stopColor="#a7f3d0"/>
            <stop offset="100%" stopColor="#059669"/>
          </radialGradient>
          <radialGradient id={`${id}-p`} cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#34d399" stopOpacity="0.2"/>
            <stop offset="100%" stopColor="#09090b" stopOpacity="0"/>
          </radialGradient>
        </defs>
        {!transparent && <rect width="100" height="100" fill="#09090b" rx="16"/>}
        <circle cx="50" cy="50" r="42" fill="none" stroke="#34d399" strokeWidth="0.8" opacity="0.2"/>
        <circle cx="50" cy="50" r="32" fill="none" stroke="#34d399" strokeWidth="0.5" opacity="0.1" strokeDasharray="2 5"/>
        <circle cx="50" cy="50" r="20" fill="none" stroke="#34d399" strokeWidth="0.5" opacity="0.12" strokeDasharray="1 4"/>
        <circle cx="50" cy="50" r="42" fill={p}/>
        <g filter={glow} opacity="0.7">
          <circle cx="50" cy="8"  r="2.5" fill="#34d399"/>
          <circle cx="92" cy="50" r="2"   fill="#2dd4bf"/>
          <circle cx="50" cy="92" r="2.5" fill="#34d399"/>
          <circle cx="8"  cy="50" r="2"   fill="#2dd4bf"/>
          <circle cx="79" cy="21" r="1.8" fill="#34d399"/>
          <circle cx="79" cy="79" r="1.8" fill="#2dd4bf"/>
          <circle cx="21" cy="79" r="1.8" fill="#34d399"/>
          <circle cx="21" cy="21" r="1.8" fill="#2dd4bf"/>
        </g>
        <g filter={glow} opacity="0.75">
          <circle cx="50" cy="18" r="2"   fill="#34d399"/>
          <circle cx="82" cy="50" r="1.8" fill="#2dd4bf"/>
          <circle cx="50" cy="82" r="2"   fill="#34d399"/>
          <circle cx="18" cy="50" r="1.8" fill="#2dd4bf"/>
        </g>
        <g stroke="#34d399" strokeWidth="0.5" opacity="0.2">
          <line x1="50" y1="50" x2="50" y2="8"/>
          <line x1="50" y1="50" x2="92" y2="50"/>
          <line x1="50" y1="50" x2="50" y2="92"/>
          <line x1="50" y1="50" x2="8"  y2="50"/>
          <line x1="50" y1="50" x2="79" y2="21"/>
          <line x1="50" y1="50" x2="79" y2="79"/>
          <line x1="50" y1="50" x2="21" y2="79"/>
          <line x1="50" y1="50" x2="21" y2="21"/>
          <line x1="50" y1="8"  x2="79" y2="21" opacity="0.5"/>
          <line x1="79" y1="21" x2="92" y2="50" opacity="0.5"/>
          <line x1="92" y1="50" x2="79" y2="79" opacity="0.5"/>
          <line x1="79" y1="79" x2="50" y2="92" opacity="0.5"/>
          <line x1="50" y1="92" x2="21" y2="79" opacity="0.5"/>
          <line x1="21" y1="79" x2="8"  y2="50" opacity="0.5"/>
          <line x1="8"  y1="50" x2="21" y2="21" opacity="0.5"/>
          <line x1="21" y1="21" x2="50" y2="8"  opacity="0.5"/>
        </g>
        <circle cx="50" cy="50" r="10" fill="none" stroke="#34d399" strokeWidth="0.8" opacity="0.3"/>
        <circle cx="50" cy="50" r="6"  fill="none" stroke="#34d399" strokeWidth="0.6" opacity="0.4"/>
        <circle cx="50" cy="50" r="3.5" fill={ng} filter={glow}/>
      </svg>
    )
  }

  // variant === 'full'
  const w = size ?? 160
  const h = Math.round(w * 560 / 500)
  const glowHard = `url(#${id}-glow-hard)`
  const glowSoft = `url(#${id}-glow-soft)`
  const glowText = `url(#${id}-glow-text)`
  const pulse    = `url(#${id}-pulse)`
  const nodeGrad = `url(#${id}-nodeGrad)`

  return (
    <svg viewBox="0 0 500 560" width={w} height={h} className={className}>
      <defs>
        <filter id={`${id}-glow-hard`}>
          <feGaussianBlur stdDeviation="5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id={`${id}-glow-soft`}>
          <feGaussianBlur stdDeviation="2.5" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id={`${id}-glow-text`}>
          <feGaussianBlur stdDeviation="3" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <radialGradient id={`${id}-pulse`} cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#34d399" stopOpacity="0.18"/>
          <stop offset="60%"  stopColor="#0d9488" stopOpacity="0.06"/>
          <stop offset="100%" stopColor="#09090b" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id={`${id}-nodeGrad`} cx="30%" cy="30%" r="70%">
          <stop offset="0%"   stopColor="#a7f3d0"/>
          <stop offset="100%" stopColor="#059669"/>
        </radialGradient>
      </defs>

      {!transparent && <rect width="500" height="560" fill="#09090b"/>}
      <ellipse cx="250" cy="235" rx="200" ry="200" fill={pulse}/>

      <circle cx="250" cy="235" r="185" fill="none" stroke="#34d399" strokeWidth="0.8" opacity="0.18"/>
      <g transform="translate(250,235)">
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(0)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(45)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(90)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(135)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(180)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(225)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(270)"/>
        <line x1="0" y1="-185" x2="0" y2="-175" stroke="#34d399" strokeWidth="1.2" opacity="0.5" transform="rotate(315)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(10)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(20)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(30)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(40)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(50)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(60)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(70)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(80)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(100)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(110)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(120)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(130)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(140)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(150)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(160)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(170)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(190)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(200)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(210)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(220)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(230)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(240)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(250)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(260)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(280)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(290)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(300)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(310)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(320)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(330)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(340)"/>
        <line x1="0" y1="-185" x2="0" y2="-179" stroke="#34d399" strokeWidth="0.6" opacity="0.25" transform="rotate(350)"/>
      </g>

      <circle cx="250" cy="235" r="145" fill="none" stroke="#34d399" strokeWidth="0.5" opacity="0.08" strokeDasharray="3 9"/>
      <circle cx="250" cy="235" r="95"  fill="none" stroke="#34d399" strokeWidth="0.5" opacity="0.10" strokeDasharray="2 7"/>
      <circle cx="250" cy="235" r="48"  fill="none" stroke="#34d399" strokeWidth="0.5" opacity="0.14" strokeDasharray="2 5"/>

      <g stroke="#34d399" strokeWidth="0.7" opacity="0.22">
        <line x1="250" y1="235" x2="250" y2="90"/>
        <line x1="250" y1="235" x2="352" y2="133"/>
        <line x1="250" y1="235" x2="395" y2="235"/>
        <line x1="250" y1="235" x2="352" y2="337"/>
        <line x1="250" y1="235" x2="250" y2="380"/>
        <line x1="250" y1="235" x2="148" y2="337"/>
        <line x1="250" y1="235" x2="105" y2="235"/>
        <line x1="250" y1="235" x2="148" y2="133"/>
        <line x1="250" y1="90"  x2="352" y2="133" opacity="0.5"/>
        <line x1="352" y1="133" x2="395" y2="235" opacity="0.5"/>
        <line x1="395" y1="235" x2="352" y2="337" opacity="0.5"/>
        <line x1="352" y1="337" x2="250" y2="380" opacity="0.5"/>
        <line x1="250" y1="380" x2="148" y2="337" opacity="0.5"/>
        <line x1="148" y1="337" x2="105" y2="235" opacity="0.5"/>
        <line x1="105" y1="235" x2="148" y2="133" opacity="0.5"/>
        <line x1="148" y1="133" x2="250" y2="90"  opacity="0.5"/>
        <line x1="250" y1="90"  x2="395" y2="235" opacity="0.2"/>
        <line x1="395" y1="235" x2="250" y2="380" opacity="0.2"/>
        <line x1="250" y1="380" x2="105" y2="235" opacity="0.2"/>
        <line x1="105" y1="235" x2="250" y2="90"  opacity="0.2"/>
        <line x1="352" y1="133" x2="352" y2="337" opacity="0.2"/>
        <line x1="148" y1="133" x2="148" y2="337" opacity="0.2"/>
      </g>
      <g stroke="#34d399" strokeWidth="0.6" opacity="0.18">
        <line x1="250" y1="235" x2="250" y2="140"/>
        <line x1="250" y1="235" x2="332" y2="187"/>
        <line x1="250" y1="235" x2="332" y2="283"/>
        <line x1="250" y1="235" x2="250" y2="330"/>
        <line x1="250" y1="235" x2="168" y2="283"/>
        <line x1="250" y1="235" x2="168" y2="187"/>
        <line x1="250" y1="140" x2="332" y2="187" opacity="0.6"/>
        <line x1="332" y1="187" x2="332" y2="283" opacity="0.6"/>
        <line x1="332" y1="283" x2="250" y2="330" opacity="0.6"/>
        <line x1="250" y1="330" x2="168" y2="283" opacity="0.6"/>
        <line x1="168" y1="283" x2="168" y2="187" opacity="0.6"/>
        <line x1="168" y1="187" x2="250" y2="140" opacity="0.6"/>
      </g>
      <g stroke="#34d399" strokeWidth="0.7" opacity="0.3">
        <line x1="250" y1="235" x2="250" y2="187"/>
        <line x1="250" y1="235" x2="298" y2="235"/>
        <line x1="250" y1="235" x2="250" y2="283"/>
        <line x1="250" y1="235" x2="202" y2="235"/>
        <line x1="250" y1="187" x2="298" y2="235" opacity="0.7"/>
        <line x1="298" y1="235" x2="250" y2="283" opacity="0.7"/>
        <line x1="250" y1="283" x2="202" y2="235" opacity="0.7"/>
        <line x1="202" y1="235" x2="250" y2="187" opacity="0.7"/>
      </g>
      <g stroke="#34d399" strokeWidth="0.5" opacity="0.12">
        <line x1="250" y1="187" x2="250" y2="140"/>
        <line x1="298" y1="235" x2="332" y2="187"/>
        <line x1="250" y1="283" x2="250" y2="330"/>
        <line x1="202" y1="235" x2="168" y2="187"/>
        <line x1="250" y1="140" x2="352" y2="133"/>
        <line x1="332" y1="187" x2="395" y2="235"/>
        <line x1="332" y1="283" x2="352" y2="337"/>
        <line x1="250" y1="330" x2="250" y2="380"/>
        <line x1="168" y1="283" x2="148" y2="337"/>
        <line x1="168" y1="187" x2="148" y2="133"/>
      </g>

      <g filter={glowSoft}>
        <circle cx="250" cy="90"  r="4.5" fill="#34d399" opacity="0.7"/>
        <circle cx="352" cy="133" r="3.5" fill="#2dd4bf" opacity="0.65"/>
        <circle cx="395" cy="235" r="4"   fill="#34d399" opacity="0.6"/>
        <circle cx="352" cy="337" r="3"   fill="#2dd4bf" opacity="0.55"/>
        <circle cx="250" cy="380" r="4"   fill="#34d399" opacity="0.6"/>
        <circle cx="148" cy="337" r="3"   fill="#2dd4bf" opacity="0.55"/>
        <circle cx="105" cy="235" r="4.5" fill="#34d399" opacity="0.65"/>
        <circle cx="148" cy="133" r="3.5" fill="#2dd4bf" opacity="0.6"/>
      </g>
      <g filter={glowSoft}>
        <circle cx="250" cy="140" r="3.5" fill="#34d399" opacity="0.75"/>
        <circle cx="332" cy="187" r="3"   fill="#2dd4bf" opacity="0.7"/>
        <circle cx="332" cy="283" r="3"   fill="#34d399" opacity="0.65"/>
        <circle cx="250" cy="330" r="3.5" fill="#2dd4bf" opacity="0.7"/>
        <circle cx="168" cy="283" r="3"   fill="#34d399" opacity="0.65"/>
        <circle cx="168" cy="187" r="3"   fill="#2dd4bf" opacity="0.7"/>
      </g>
      <g filter={glowSoft}>
        <circle cx="250" cy="187" r="4"   fill="#34d399" opacity="0.85"/>
        <circle cx="298" cy="235" r="3.5" fill="#2dd4bf" opacity="0.8"/>
        <circle cx="250" cy="283" r="4"   fill="#34d399" opacity="0.85"/>
        <circle cx="202" cy="235" r="3.5" fill="#2dd4bf" opacity="0.8"/>
      </g>

      <circle cx="250" cy="235" r="55" fill={pulse}/>
      <circle cx="250" cy="235" r="18" fill="none" stroke="#34d399" strokeWidth="1" opacity="0.4"/>
      <circle cx="250" cy="235" r="12" fill="none" stroke="#34d399" strokeWidth="0.8" opacity="0.5"/>
      <circle cx="250" cy="235" r="7" fill={nodeGrad} filter={glowHard}/>

      <text x="250" y="440" textAnchor="middle"
        fontFamily="Georgia, 'Times New Roman', serif"
        fontSize="13" fill="#34d399" opacity="0.35" letterSpacing="6" fontStyle="italic">
        ἀνάμνησις
      </text>
      <text x="250" y="470" textAnchor="middle"
        fontFamily="'Courier New', Courier, monospace"
        fontSize="28" fontWeight="700" fill="#34d399"
        letterSpacing="10" filter={glowText} opacity="0.95">
        ANAMNESIS
      </text>
      <text x="250" y="492" textAnchor="middle"
        fontFamily="'Courier New', Courier, monospace"
        fontSize="9.5" fill="#52525b" letterSpacing="4">
        PERSISTENT MEMORY SERVER
      </text>
    </svg>
  )
}
